import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';
import { canAny } from '../../api/client';
import { fetchProjects } from '../../api/ops';
import { useMe } from '../auth/session';
import { useSiteScope } from '../../app/scope';
import { useSites } from '../../data/hooks';
import type { Site } from '../../data/source';
import { Field } from '../../ui/components';

/** Client + project the reports pages work on (kept while navigating between their tabs). */
export const useReportScope = create<{
  clientId: string | null;
  projectId: string | null;
  set(p: { clientId?: string | null; projectId?: string | null }): void;
}>((set) => ({ clientId: null, projectId: null, set: (p) => set(p) }));

/** The client (root) of a site. */
export function clientOf(sites: Site[], siteId: string | null): string | null {
  let s = sites.find((x) => x.id === siteId);
  while (s?.parentId) s = sites.find((x) => x.id === s!.parentId);
  return s?.id ?? null;
}

/**
 * Client and project selectors; follows the site chosen in the top bar. The authority observer (no operations.view)
 * works on its whole, single client: neither selector is shown to it. Every other role keeps both.
 */
export function ReportScopePicker({ withProject: wanted = true }: { withProject?: boolean }) {
  const { t } = useTranslation();
  const ops = canAny(useMe().data, 'operations.view');
  const withProject = wanted && ops;
  const sites = useSites().data ?? [];
  const clients = sites
    .filter((s) => s.parentId === null && s.kind === 'organization')
    .sort((a, b) => a.name.localeCompare(b.name, 'ro'));
  const topSite = useSiteScope((s) => s.siteId);
  const { clientId, projectId, set } = useReportScope();
  useEffect(() => {
    const fromTop = clientOf(sites, topSite);
    if (fromTop && fromTop !== clientId) set({ clientId: fromTop, projectId: null });
    else if (!clientId && clients[0]) set({ clientId: clients[0].id, projectId: null });
  }, [topSite, sites.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const projects = useQuery({
    queryKey: ['projects', clientId],
    queryFn: () => fetchProjects(clientId),
    enabled: !!clientId && withProject,
  });
  if (!ops) return null;
  return (
    <div className="inline-form">
      {ops && (
        <Field label={t('reportsPage.client')}>
          <select
            className="input"
            value={clientId ?? ''}
            onChange={(e) => set({ clientId: e.target.value || null, projectId: null })}
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      {withProject && (
        <Field label={t('reportsPage.project')}>
          <select
            className="input"
            value={projectId ?? ''}
            onChange={(e) => set({ projectId: e.target.value || null })}
          >
            <option value="">{t('reportsPage.wholeClient')}</option>
            {(projects.data?.items ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.code ? `${p.name} (${p.code})` : p.name}
              </option>
            ))}
          </select>
        </Field>
      )}
    </div>
  );
}
